import { LEGAL } from "@/config/legal";
import { RotateCcw, CheckCircle2, XCircle, Info } from "lucide-react";

const H = ({ children }) => (
  <h2 className="mt-10 font-serif text-2xl md:text-3xl text-[#0A1128]">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">{children}</p>
);

export default function Refund() {
  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="refund-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Legal</div>
        </div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128]">Refund Policy</h1>
        <div className="mt-3 text-xs text-slate-500">
          Effective {LEGAL.effectiveDate} · Last updated {LEGAL.lastUpdated}
        </div>

        <P>
          {LEGAL.brandMark} is committed to fair, transparent billing. This Refund Policy explains when refunds are
          available and how to request one. It applies to purchases made through {LEGAL.companyLegalName} on the{" "}
          {LEGAL.brandMark} platform.
        </P>

        {/* Quick-reference cards */}
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="mt-3 font-serif text-lg text-[#0A1128]">Certification rejected</div>
            <div className="mt-1 text-xs text-slate-500">Full refund, automatic, within 5 business days.</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <Info className="w-5 h-5 text-[#0A1128]" />
            <div className="mt-3 font-serif text-lg text-[#0A1128]">Subscription cancelled</div>
            <div className="mt-1 text-xs text-slate-500">Access through paid period. No pro-ration on monthly.</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <XCircle className="w-5 h-5 text-slate-400" />
            <div className="mt-3 font-serif text-lg text-[#0A1128]">Certification approved</div>
            <div className="mt-1 text-xs text-slate-500">Non-refundable once issued — service already rendered.</div>
          </div>
        </div>

        <H>1. Certifications ({LEGAL.pricing[0].price}, {LEGAL.pricing[1].price}, {LEGAL.pricing[2].price} upgrade)</H>
        <P>
          Certification fees are charged at the time you submit an application. Because certification requires manual
          review, once we have <b>approved and issued</b> a certification, the fee is <b>non-refundable</b>.
        </P>
        <P>
          If we <b>reject</b> your certification application, you will receive a <b>full automatic refund</b> to your
          original payment method within 5 business days. No action needed on your part.
        </P>
        <P>
          The {LEGAL.pricing[2].price} Standard → Premium upgrade fee is non-refundable once the upgraded tier is applied.
        </P>

        <H>2. Monthly Monitoring ({LEGAL.pricing[3].price})</H>
        <P>
          Monthly Monitoring subscriptions renew automatically each month. You may cancel at any time from your Host
          Dashboard or by emailing{" "}
          <a href={`mailto:${LEGAL.supportEmail}`} className="text-emerald-700 underline">{LEGAL.supportEmail}</a>.
          Cancellation stops future renewals; you retain access for the remainder of the paid period. We do not offer
          partial-month or pro-rated refunds on Monthly Monitoring.
        </P>

        <H>3. Yearly Monitoring ({LEGAL.pricing[4].price})</H>
        <P>
          Yearly Monitoring subscriptions renew automatically each year and offer a discounted rate. If you cancel
          within the <b>first 30 days</b> of a new yearly term and have not filed any certification updates, you may
          request a full refund. After 30 days, we offer a <b>pro-rated refund</b> for the unused months, minus a
          10% administrative fee.
        </P>

        <H>4. Guest Purchases</H>
        <P>
          Guests do not currently transact with {LEGAL.brandMark}; bookings occur on the third-party booking platform
          (Airbnb, Vrbo, Booking.com, etc.). Refunds for bookings are subject to those platforms&apos; own policies.
        </P>

        <H>5. Duplicate or Erroneous Charges</H>
        <P>
          If you notice a duplicate charge or a charge you did not authorize, please email{" "}
          <a href={`mailto:${LEGAL.supportEmail}`} className="text-emerald-700 underline">{LEGAL.supportEmail}</a> within
          60 days of the charge. We investigate and, if confirmed, issue a full refund within 5 business days.
        </P>

        <H>6. Chargebacks</H>
        <P>
          If you dispute a charge with your card issuer without first contacting us, we may suspend your account until
          the dispute is resolved. Please contact us first — we resolve nearly all issues within 24 hours.
        </P>

        <H>7. How to Request a Refund</H>
        <P>Email us at{" "}
          <a href={`mailto:${LEGAL.supportEmail}`} className="text-emerald-700 underline">{LEGAL.supportEmail}</a>{" "}
          from the address on your account, and include:
        </P>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li>Your account email;</li>
          <li>The last 4 digits of the card charged;</li>
          <li>Approximate date &amp; amount of the charge;</li>
          <li>Reason for the refund request.</li>
        </ul>
        <P>
          We respond within 2 business days. Approved refunds appear on your original payment method within 5–10
          business days, depending on your bank.
        </P>

        <H>8. Changes to This Policy</H>
        <P>
          We may update this Refund Policy from time to time. Changes apply only to charges made after the effective
          date of the update.
        </P>

        <p className="mt-10 text-xs text-slate-500">
          This Refund Policy is part of, and incorporated by reference into, our{" "}
          <a href="/terms" className="text-emerald-700 underline">Terms of Service</a>.
        </p>
      </div>
    </div>
  );
}
